// modules/pricing/api/pricingApi.ts
import { rpost, rget, DEV_URL } from "utils/requests";

const PROXY_BASE_URL = `${DEV_URL}/api/PricingProxy`;

const getProxyHeaders = (userToken: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${userToken}`,
});

const AllSelectedDropdownValue = {
  name: "All",
  value: "All",
  isSelected: false,
};

const getUtilityIdentifier = (utility: any) =>
  utility?.EnerexIdentifier ??
  utility?.enerexIdentifier ??
  utility?.enerexUtilityIdenitifier ??
  utility?.id ??
  utility?.value ??
  utility?.LongName ??
  utility?.longName;

const getUtilityName = (utility: any) =>
  utility?.ShortName ??
  utility?.shortName ??
  utility?.LongName ??
  utility?.longName ??
  utility?.utilityName ??
  utility?.name ??
  utility?.value ??
  "";

export const fetchUtilitiesByStateApi = async (params: {
  stateIdentifier?: string;
  commodity?: string;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  const stateIdentifier = params?.stateIdentifier;
  if (!params?.userToken) {
    throw new Error("Missing user token for utilities-by-state request");
  }
  if (!stateIdentifier) {
    return [AllSelectedDropdownValue];
  }

  const payload = {
    enerexStateIdentifier: stateIdentifier,
    commodity: params?.commodity || "Electric",
  };

  const response = await fetch(`${PROXY_BASE_URL}/utility`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(payload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch utilities by state");
  }

  const data = await response.json();
  const utilitiesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];

  const utilities = utilitiesArray.map((utility: any) => ({
    id: getUtilityIdentifier(utility),
    value: getUtilityIdentifier(utility),
    name: getUtilityName(utility),
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...utilities];
};

export const fetchStatesByUtilityApi = async (params: {
  utilityIdentifier?: string;
  commodity?: string;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  const utilityIdentifier = params?.utilityIdentifier;
  if (!params?.userToken) {
    throw new Error("Missing user token for states-by-utility request");
  }
  if (!utilityIdentifier) {
    return [AllSelectedDropdownValue];
  }

  const payload = {
    enerexUtilityIdentifiers: utilityIdentifier,
    commodity: params?.commodity || "Electric",
  };

  const response = await fetch(`${PROXY_BASE_URL}/state`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(payload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch states by utility");
  }

  const data = await response.json();
  const statesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];

  const states = statesArray.map((item: any) => ({
    id:
      item?.EnerexStateIdentifier ??
      item?.enerexStateIdentifier ??
      item?.id ??
      item?.value ??
      item?.stateName,
    value:
      item?.EnerexStateIdentifier ??
      item?.enerexStateIdentifier ??
      item?.id ??
      item?.value ??
      item?.stateName,
    name: item?.stateName ?? item?.name ?? item?.Name ?? item?.value ?? "",
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...states];
};

export const fetchUtilitiesApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for utilities request");
  }
  // Map commodity to 'electric' or 'NatGas' for utilities API (PricingMatrix)
  const commodity = (() => {
    const c = params?.commodity;
    if (
      c === "Electricity" ||
      c === "Electric" ||
      c === "electric" ||
      c === 1 ||
      c === "1"
    )
      return "Electric";
    if (
      c === "NatGas" ||
      c === "Gas" ||
      c === "Nat Gas" ||
      c === 2 ||
      c === "2"
    )
      return "NatGas";
    return "electric"; // default to electric
  })();

  const selectedState = params?.state || "All";

  if (selectedState === "All") {
    return [AllSelectedDropdownValue];
  }
  const apiPayload = {
    state: [params.state || ""],
    commodity,
  };
  const response = await fetch(`${PROXY_BASE_URL}/utilities`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
  });

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error("Failed to fetch utilities");
  const data = await response.json();

  const utilities = data.map((utility: any) => ({
    id: getUtilityIdentifier(utility),
    value: getUtilityIdentifier(utility),
    name: getUtilityName(utility),
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...utilities];
};

// Market-based utilities API (for initial load and "All" state selection)
export const fetchUtilitiesByMarketApi = async (params: {
  marketIdentifier?: string;
  commodity?: string;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  const marketIdentifier = params?.marketIdentifier;
  const commodity = params?.commodity || "Electric";
  if (!params?.userToken) {
    throw new Error("Missing user token for utilities-by-market request");
  }

  const payload = {
    marketEnerexIdentifier: marketIdentifier || null,
    commodity: commodity,
  };

  const response = await fetch(`${PROXY_BASE_URL}/utilities/market`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(payload),
    signal: params?.signal,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch utilities by market");
  }

  const data = await response.json();
  const utilitiesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];

  const utilities = utilitiesArray.map((utility: any) => ({
    id: getUtilityIdentifier(utility),
    value: getUtilityIdentifier(utility),
    name: getUtilityName(utility),
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...utilities];
};

export const fetchLoadZonesApi = async (params: any): Promise<any> => {
  const enerexStateIdentifier = params?.stateIdentifier;

  if (!enerexStateIdentifier || enerexStateIdentifier === "All") {
    return [AllSelectedDropdownValue];
  }
  if (!params?.userToken) {
    throw new Error("Missing user token for load zones request");
  }
  const response = await fetch(`${PROXY_BASE_URL}/loadzones`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify({ stateIdentifier: enerexStateIdentifier }),
    signal: params?.signal,
  });

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error("Failed to fetch loadzones");
  const data = await response.json();

  const loadZones = data
    .map((item: any) => ({
      id: item.enerexIdentifier,
      value: item.enerexIdentifier,
      name: item.name,
      isSelected: false,
    }))
    .sort((a: any, b: any) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      })
    );

  return [AllSelectedDropdownValue, ...loadZones];
};

export const fetchStatesApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for states request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/states`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error("Failed to fetch states list");
  const data = await response.json();

  const stateList = data.map((item: any) => ({
    id: item.EnerexStateIdentifier,
    value: item.EnerexStateIdentifier,
    name: item.Name,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...stateList];
};

export const fetchStatesByMarketApi = async (params: any): Promise<any> => {
  const marketEnerexIdentifier = params?.marketEnerexIdentifier || "";
  const commodity = params?.commodity || "";
  if (!marketEnerexIdentifier) return [AllSelectedDropdownValue];

  if (!params?.userToken) {
    throw new Error("Missing user token for market states request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/marketstates`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify({
      marketEnerexIdentifier,
      commodity,
    }),
    signal: params?.signal,
  });

  if (!response.ok) throw new Error("Failed to fetch states by market");
  const data = await response.json();

  // Handle case where data might not be an array
  let statesArray = [];
  if (Array.isArray(data)) {
    statesArray = data;
  } else if (data && Array.isArray(data.states)) {
    statesArray = data.states;
  } else if (data && Array.isArray(data.data)) {
    statesArray = data.data;
  }

  const stateList = statesArray.map((item: any) => ({
    id: item.enerexStateIdentifier,
    value: item.enerexStateIdentifier,
    name: item.stateName,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...stateList];
};

export const fetchBillingMethodApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for billing methods request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/billingmethods`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error("Failed to fetch billing methods");
  const data = await response.json();

  const billingMethods = data.map((item: any) => ({
    id: item.shortName,
    value: item.shortName,
    name: item.shortName,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...billingMethods];
};

// export const fetchProductTypeApi = async (params: any): Promise<any> => {
//   return ["Fixed", "Index"];
// };

export const fetchProductTypeApi = async (params: {
  commodity?: string | number;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for product types request");
  }

  // Map commodity to string value (Electric or NatGas)
  const commodity = (() => {
    const c = params?.commodity;
    if (
      c === 1 ||
      c === "1" ||
      `${c}`.toLowerCase().includes("electric") ||
      `${c}`.toLowerCase().includes("electricity")
    ) {
      return "Electric";
    }
    if (
      c === 2 ||
      c === "2" ||
      `${c}`.toLowerCase().includes("gas") ||
      `${c}`.toLowerCase().includes("natgas")
    ) {
      return "NatGas";
    }
    return "Electric"; // default to Electric
  })();

  const response = await fetch(`${PROXY_BASE_URL}/producttypes/${commodity}`, {
    method: "GET",
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch product types");
  }

  const data = await response.json();

  // Handle different response formats
  const productTypesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];

  // Transform to expected format
  const productTypes = productTypesArray.map((item: any) => {
    // Handle string values
    if (typeof item === "string") {
      return {
        id: item,
        value: item,
        name: item,
        isSelected: false,
      };
    }
    // Handle object format with EnerexIdentifier, Name, Commodity
    return {
      id: item?.EnerexIdentifier || item?.id,
      value: item?.Name || item?.name || item?.value,
      name: item?.Name || item?.name || item?.value,
      isSelected: false,
    };
  });

  return productTypes;
};

export const fetchLoadProfilesApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for load profiles request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/loadprofiles`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });

  if (!response.ok) throw new Error("Failed to fetch load profiles");
  const data = await response.json();

  const loadProfiles = data.map((item: any) => ({
    id: item.value,
    value: item.value,
    name: item.name,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...loadProfiles];
};
const STATIC_COST_COMPONENT_OPTIONS = [
  {
    id: "static-cost-component-all",
    value: "All",
    name: "All",
    isSelected: false,
  },
  {
    id: "static-cost-component-no-pass-through",
    value: "All Inclusive",
    name: "All Inclusive",
    isSelected: false,
    isBold: true,
  },
  {
    id: "static-cost-component-all-passthroughs",
    value: "All Passthroughs",
    name: "All Passthroughs",
    isSelected: false,
    isBold: true,
  },
];

const mergeStaticCostComponents = (list: any[] = []) => {
  const existingValues = new Set(list.map((item) => item?.value));
  const staticOptions = STATIC_COST_COMPONENT_OPTIONS.filter(
    (option) => !existingValues.has(option.value)
  );
  return [...staticOptions, ...list];
};

export const fetchCostComponentsApi = async (params: {
  commodity?: string | number;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for cost components request");
  }

  const commodityLabel = (() => {
    // const commodity = (() => {
    //     const c = params?.commodity;
    //     if (
    //       c === "Electricity" ||
    //       c === "Electric" ||
    //       c === "electric" ||
    //       c === 1 ||
    //       c === "1"
    //     )
    //       return "Electric";
    //     if (
    //       c === "NatGas" ||
    //       c === "Gas" ||
    //       c === "Nat Gas" ||
    //       c === 2 ||
    //       c === "2"
    //     )
    //       return "NatGas";
    //     return "electric"; // default to electric
    //   })();
    const c = params?.commodity;
    if (
      c === 1 ||
      c === "1" ||
      `${c}`.toLowerCase().includes("electric") ||
      `${c}`.toLowerCase().includes("electricity")
    ) {
      return "Electric";
    }
    if (
      c === 2 ||
      c === "2" ||
      `${c}`.toLowerCase().includes("gas") ||
      `${c}`.toLowerCase().includes("natgas")
    ) {
      return "NatGas";
    }
    return "Electric";
  })();

  const response = await fetch(`${PROXY_BASE_URL}/bycommodity`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify({ commodity: commodityLabel }),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch cost components");
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    return [];
  }

  const normalized = data.map((item: any) => ({
    id: item?.enerexIdentifier || item?.name,
    value: item?.name,
    name: item?.name,
    enerexIdentifier: item?.enerexIdentifier,
    isBold: item?.name === "All Inclusive",
    isSelected: false,
  }));

  return mergeStaticCostComponents(normalized);
};

export const fetchMarketsApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for markets request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/markets`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(
      params.commodity ? { commodity: params.commodity } : {}
    ),
    signal: params?.signal,
  });

  if (!response.ok) throw new Error("Failed to fetch markets");
  const data = await response.json();

  const markets =
    data.Markets?.map((item: any) => ({
      id: item.Market,
      value: item.EnerexIdentifier,
      name: item.Market,
      commodity: item.Commodity,
      isSelected: false,
    })) || [];

  return markets;
};

// export const fetchSuppliersApi = async (state: string, utility: string, loadZone: string): Promise<any> => {
//   const response = await fetch(`${BASE_URL}/suppliers?state=${state}&utility=${utility}&loadZone=${loadZone}`);
//   if (!response.ok) throw new Error('Failed to fetch suppliers');
//   return response.json();
// };

export const fetchRateClassesApi = async (params: any): Promise<any> => {
  let utilityIdentifier = params?.utilityIdentifier;
  // Map commodity to '1' or '2' for rate classes API (MiddleEarth: 1=NatGas, 2=Electric)
  const commodity = (() => {
    const c = params?.commodity;
    if (c === "Electricity" || c === "Electric" || c === 2 || c === "2")
      return "2";
    if (
      c === "NatGas" ||
      c === "Gas" ||
      c === "Nat Gas" ||
      c === 1 ||
      c === "1"
    )
      return "1";
    return "2"; // default to electric
  })();

  if (!utilityIdentifier || utilityIdentifier === "All") {
    return [];
  }

  const apiPayload = {
    enerexUtilityIdentifier: utilityIdentifier,
    commodity,
  };

  if (!params?.userToken) {
    throw new Error("Missing user token for rate classes request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/rateclasses`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
  });

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error("Failed to fetch rateclasses");
  const data = await response.json();

  const rateClasses = data.map((item: any) => ({
    id: item.rateClassName,
    value: item.rateClassName,
    name: item.rateClassName,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...rateClasses];
};

/** */

// export const fetchTermBasedPricingChartData = async (isV2=false): Promise<any> => {
//   const postRequestParams = {}
//   const response = await rget(
//     `${DEV_URL}/api/PricingMatrix/GetECToken`,
//     postRequestParams
// );
//   if (response.status != 200) throw new Error('Failed to fetch pricing data');
//   const data = await response.data;

//   const chartsData = data.map((chart: any) => {

//     const categories = chart?.dataSets[0]?.dataPoints?.map( item => item?.label);
//     const series = chart?.dataSets?.map(dataset => {
//       const data = dataset?.dataPoints?.map(point => point.value);
//       return {...dataset, data}
//     });
//     return {
//       title: chart?.title,
//       categories,
//       series
//     }
//   });

//   return chartsData;
// };

const getPricingMatrixApiPayload = (params) => {
  const taxIncluded =
    typeof params.taxIncluded === "boolean" ? params.taxIncluded : null;
  const por = typeof params.por === "boolean" ? params.por : null;
  const wrap = (val) => {
    if (Array.isArray(val)) {
      const normalized = val
        .filter(Boolean)
        .map((v) =>
          typeof v === "object" && v !== null
            ? v.value ?? v.id ?? v.name ?? String(v)
            : String(v)
        )
        .map((s) => s.trim())
        .filter(
          (s) => s && s.toLowerCase() !== "all" && s.toLowerCase() !== "none"
        );
      return normalized;
    }
    if (!val) return [];
    const s = String(val).trim();
    return s && s.toLowerCase() !== "all" && s.toLowerCase() !== "none"
      ? [s]
      : [];
  };

  // Format date to YYYY-MM-DD format
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0]; // Get YYYY-MM-DD format
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Normalize commodity to required numeric values for charts API (1=electric, 2=NatGas)
  const normalizedCommodity = (() => {
    const c = params.commodity;
    if (
      c === 1 ||
      c === "1" ||
      c === "Electricity" ||
      c === "Electric" ||
      c === "electric"
    )
      return 1;
    if (
      c === 2 ||
      c === "2" ||
      c === "NatGas" ||
      c === "Gas" ||
      c === "Nat Gas"
    )
      return 2;
    return "";
  })();

  // Check if gas commodity - don't send LoadZone for gas
  const isGasCommodity = normalizedCommodity === 2;
  const startToIso = formatDate(params.startToDate) || null;
  const effectiveToIso = formatDate(params.effectiveToDate) || null;
  // debugger;
  return {
    Suppliers: [], //params.Suppliers || [],
    State: wrap(params.selectedStates),
    Terms: wrap(params.selectedTerm),
    RateClass: wrap(params.selectedRateClass),
    LoadProfile: wrap(params.selectedLoadProfile),
    LoadZone: isGasCommodity ? [] : wrap(params.selectedLoadZone),
    BillingMethod: wrap(params.selectedBillingMethod),
    Utilities: wrap(params.selectedUtility),
    Usage:
      params?.usage !== null &&
      params?.usage !== undefined &&
      Number.isFinite(params.usage)
        ? Math.floor(params.usage)
        : null,
    Demand:
      params?.demand !== null &&
      params?.demand !== undefined &&
      Number.isFinite(params.demand)
        ? Math.floor(params.demand)
        : null,
    LoadFactor:
      params?.loadFactor !== null &&
      params?.loadFactor !== undefined &&
      Number.isFinite(params.loadFactor)
        ? Number(params.loadFactor)
        : null,
    ProductType: params.selectedProductType || [],
    CostComponent: params.passThrough || [],
    NumberOfMonth:
      params.noOfMonths !== undefined && params.noOfMonths !== null
        ? Number(params.noOfMonths)
        : null,
    ContractStartFrom: null,
    IsNotAnonymized: params.IsNotAnonymized,
    ContractStartTo: startToIso,
    EffectiveFrom: null,
    EffectiveTo: effectiveToIso,
    Commodity: normalizedCommodity,
    EnerexClientIdentifier: params.isSystemAdmin
      ? // false
        params.selectedClientIdentifier || null
      : params.clientId || null,
    MarketEnerexIdentifier: params.MarketEnerexIdentifier,
    CustomerClass: params.customerClass || null,
    ...(taxIncluded !== null ? { taxIncluded } : {}),
    ...(por !== null ? { POR: por } : {}),
  };
};

export const fetchPriceTrendChartData = async (params: any): Promise<any> => {
  const Suppliers = [
    "6bd2c20f-0ce3-4b00-acde-63569a4273a1",
    // "374BD0E8-60AC-4A6A-BE86-EF7191E47047",
    // "38E36FC8-0C9A-4774-9632-555E6C28BB37",
    "958A3FC6-21D4-430F-8834-209E801A7C39",
    // "875C7031-92CB-4ABE-811F-1733A049EBF4",
    "9FEDD1C8-EB97-404C-9E3C-7A267473CCD4",
    "60bb3692-528d-4b1a-8af9-07e46f55b2b4",
  ].map((id) => id.toLowerCase());
  const apiPayload = getPricingMatrixApiPayload({ Suppliers, ...params });
  if (!params?.userToken) {
    throw new Error("Missing user token for pricing trend request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/pricingtrend`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  const jsonData = await response.json();
  const sortedSuppliers = jsonData;
  // .sort(
  //   (a,b) => a.Supplier.localeCompare(b.Supplier)
  // );

  return sortedSuppliers;
};

export const fetchPriceTrendMinMaxChartData = async (
  params: any
): Promise<any> => {
  const apiPayload = getPricingMatrixApiPayload(params);
  if (!params?.userToken) {
    throw new Error("Missing user token for pricing range request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/pricingrange`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  return await response.json();
};

export const fetchForwardCurveChartData = async (params: any): Promise<any> => {
  const Suppliers = [
    "6bd2c20f-0ce3-4b00-acde-63569a4273a1",
    "958A3FC6-21D4-430F-8834-209E801A7C39",
    // "374BD0E8-60AC-4A6A-BE86-EF7191E47047",
    // "38E36FC8-0C9A-4774-9632-555E6C28BB37",
    // "875C7031-92CB-4ABE-811F-1733A049EBF4",
    "9FEDD1C8-EB97-404C-9E3C-7A267473CCD4",
    "60bb3692-528d-4b1a-8af9-07e46f55b2b4",
  ].map((id) => id.toLowerCase());

  const apiPayload = getPricingMatrixApiPayload({ Suppliers, ...params });

  if (!params?.userToken) {
    throw new Error("Missing user token for forward curve request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/forwardcurve`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  const jsonData = await response.json();
  const sortedSuppliers = jsonData;
  // .sort(
  //   (a,b) => a.Supplier.localeCompare(b.Supplier)
  // );
  return sortedSuppliers;
};

export const fetchForwadCurveMinMaxChartData = async (
  params: any
): Promise<any> => {
  const apiPayload = getPricingMatrixApiPayload(params);

  if (!params?.userToken) {
    throw new Error("Missing user token for forward range request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/forwardcurverange`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  return await response.json();
};

export const fetchSuppliersListApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for suppliers list request");
  }

  const response = await fetch(`${PROXY_BASE_URL}/suppliers`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });
  if (!response.ok) throw new Error("Failed to fetch suppliers list");
  const data = await response.json();
  // map to dropdown shape
  return (Array.isArray(data) ? data : []).map((item: any) => ({
    id: item?.SupplierIdentifier,
    value: item?.SupplierIdentifier,
    name: item?.SupplierName,
    isSelected: false,
  }));
};

export const fetchCustomerTypeApi = async (params: {
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for customer types request");
  }

  // For now, return static data as requested
  // TODO: Replace with actual API call when endpoint is available
  // const response = await fetch(`${PROXY_BASE_URL}/customertypes`, {
  //   method: "GET",
  //   headers: getProxyHeaders(params.userToken),
  //   signal: params?.signal,
  // });
  //
  // if (!response.ok) {
  //   throw new Error("Failed to fetch customer types");
  // }
  //
  // const data = await response.json();
  // const customerTypesArray = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  //
  // return customerTypesArray.map((item: any) => ({
  //   id: item?.id || item?.value,
  //   value: item?.value || item?.name,
  //   name: item?.name || item?.value,
  //   isSelected: false,
  // }));

  // Static data for now
  return [
    { id: "All", value: "All", name: "All", isSelected: false },
    {
      id: "COMM",
      value: "COMM",
      name: "Commercial",
      isSelected: false,
    },
    {
      id: "NONRESI",
      value: "NONRESI",
      name: "Non Residential",
      isSelected: false,
    },
    {
      id: "RESI",
      value: "RESI",
      name: "Residential",
      isSelected: false,
    },
  ];
};
